import { useState, useEffect, FormEvent } from "react";
import { submitLead } from "@/api/leadApi";
import { getHomeData } from "@/api/homeApi";

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [certTags, setCertTags] = useState<string[]>([]);

  // 与 footer 资质标签同源：均取自后台首页配置的 footerTags
  useEffect(() => {
    getHomeData()
      .then((res: any) => {
        const tags = (res?.footerTags || res?.data?.footerTags || [])
          .map((t: any) => t.name)
          .filter(Boolean);
        setCertTags(tags);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setLoading(true);
    const formData = new FormData(form);
    try {
      await submitLead({
        company: formData.get('company') as string,
        name: formData.get('name') as string,
        phone: formData.get('phone') as string,
        email: formData.get('email') as string || '',
        service: formData.get('service') as string,
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-10 lg:py-20 bg-[#F8F9FA]" id="contact-form">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
          {/* Left info */}
          <div className="flex-1 lg:pt-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-5">
              <span className="text-[#1a56db] text-xs font-medium">免费咨询</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0B1B3D] mb-5 leading-tight">
              获取欧格登专属企业安全方案
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-10">
              填写您的企业信息，欧格登智能的安全专家将在 1 个工作日内与您联系，为您量身定制信息安全、IT基础架构或弱电智能化解决方案与报价，首次咨询完全免费。
            </p>

            <div className="space-y-6">
              {[
                { icon: "ri-time-line", title: "快速响应", desc: "1个工作日内安全专家回访" },
                { icon: "ri-shield-check-line", title: "免费评估", desc: "提供企业安全现状免费诊断评估" },
                { icon: "ri-file-list-3-line", title: "定制方案", desc: "根据业务需求量身定制安全方案" },
                { icon: "ri-lock-password-line", title: "保密承诺", desc: "严格保护您的企业信息安全" },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1a56db]/8 flex-shrink-0">
                    <i className={`${item.icon} text-[#1a56db] text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0B1B3D] mb-0.5">{item.title}</div>
                    <div className="text-sm text-gray-500">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust badges */}
            {certTags.length > 0 && (
              <div className="mt-10 pt-8 border-t border-gray-200">
                <p className="text-xs text-gray-400 mb-3">权威认证保障</p>
                <div className="flex flex-wrap gap-2">
                  {certTags.map((cert) => (
                    <span key={cert} className="px-3 py-1.5 text-xs border border-[#1a56db]/20 text-[#1a56db] rounded-full bg-[#1a56db]/5">
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="flex-1 w-full">
            {submitted ? (
              <div className="bg-white rounded-2xl p-12 border border-gray-100 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-green-500"></i>
                </div>
                <h3 className="text-xl font-bold text-[#0B1B3D] mb-2">提交成功！</h3>
                <p className="text-gray-500 text-sm">感谢您的咨询，我们的安全专家将在1个工作日内与您联系。</p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-8 lg:p-10 border border-gray-100"
              >
                <h3 className="text-lg font-bold text-[#0B1B3D] mb-6">填写咨询信息</h3>

                {/* 企业名称 */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-[#333333] mb-1.5">
                    企业名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="请输入贵公司名称"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] placeholder-gray-400"
                  />
                </div>

                {/* 联系人 + 手机号 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1.5">
                      联系人 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="请输入联系人姓名"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1.5">
                      手机号码 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="请输入手机号码"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* 邮箱 */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-[#333333] mb-1.5">
                    邮箱地址
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="请输入邮箱（选填）"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] placeholder-gray-400"
                  />
                </div>

                {/* 感兴趣的服务 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#333333] mb-1.5">
                    感兴趣的服务 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="service"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] cursor-pointer"
                  >
                    <option value="">请选择感兴趣的服务方向</option>
                    <option value="网络安全综合防护">网络安全综合防护</option>
                    <option value="终端安全与数据保护">终端安全与数据保护</option>
                    <option value="安全运营与应急响应">安全运营与应急响应</option>
                    <option value="IT基础架构规划建设">IT基础架构规划建设</option>
                    <option value="云计算迁移与建设">云计算迁移与建设</option>
                    <option value="弱电智能化系统">弱电智能化系统</option>
                    <option value="等保合规咨询">等保合规咨询</option>
                    <option value="综合安全解决方案">综合安全解决方案</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-[#1a56db] hover:bg-[#1e40af] disabled:opacity-60 text-white font-bold rounded-lg transition-all duration-200 text-sm whitespace-nowrap cursor-pointer tracking-wide"
                >
                  {loading ? "提交中..." : "免费获取专属安全方案"}
                </button>

                <p className="text-center text-xs text-gray-400 mt-3">
                  提交即代表您同意我们的隐私政策，您的信息将被严格保密
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
